import { toast } from 'sonner';
import { Badge, SurfaceCard } from '@react-apps/design-system';
import { errorToastId, readApiErrorMessage } from '@/shared/request-feedback';
import type { BadgeProps } from '@react-apps/design-system';
import { formatInstitutionLabel } from './exam-body';
import type {
  EditLockedQuestionResult,
  Institution,
} from '../domain/entities/content.entity';
import type {
  ContentType,
  ContentStatus,
  ContentRelation,
  ExamKind,
  ExamType,
} from '../domain/entities/content.entity';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

/**
 * Extrai uma mensagem legível de um erro de request (axios) ou Error genérico.
 *
 * Uma `string` passa direto: é a mensagem **já traduzida** por quem conhece o
 * código de erro (ex.: `translateInstitutionError`, que transforma
 * `INSTITUTION_IN_USE` em "N provas apontam para ela…"). Sem essa passagem, a
 * tradução seria descartada e a tela mostraria a mensagem do domínio em inglês.
 *
 * A extração em si mora em `shared/request-feedback.ts` porque a rede global de
 * erro (`createRequestFeedbackCaches`) precisa dela e é nível de app — duas
 * cópias divergiriam na primeira vez que o back mudasse o formato do erro.
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  return translateContentLinkError(err) ?? readApiErrorMessage(err, fallback);
}

/**
 * Recusas do grafo de conteúdo que chegam por várias telas — excluir,
 * transferir, criar questão, relacionar na ficha — e por isso traduzidas aqui,
 * no ponto por onde todas passam, e não em cada `onError`. Sem isto o toast
 * mostrava a frase do domínio em inglês ("Exam … cannot CONTAINS a question").
 * O modelo por trás (a prova real é dona da questão, o simulado só aponta):
 * primum-monolith `docs/reference/modules/content.md` § Dono vs. apontador.
 */
function translateContentLinkError(err: unknown): string | undefined {
  switch (readErrorCode(err)) {
    case 'INVALID_CONTENT_LINK_EXAM_KIND':
      return 'Simulado não é dono de questão: questões nascem e são transferidas só para provas reais. O simulado aponta para questões de uma prova (relação "Aponta para").';
    case 'QUESTION_OWNER_ALREADY_EXISTS':
      return 'Esta questão já pertence a outra prova — uma questão tem uma prova dona só. Para usá-la num simulado, aponte para ela em vez de ligá-la de novo.';
    // O lock de edição: outra pessoa está com o conteúdo aberto em edição.
    // `contentIds` só vem do lote (`throwIfEditLocked`); o 409 de uma rota
    // traz um id só, em `contentId`.
    case 'CONTENT_EDIT_LOCKED': {
      const count = readErrorDetail<string[]>(err, 'contentIds')?.length ?? 1;
      return count > 1
        ? `Outra pessoa está editando ${count} destas questões, e elas ficaram como estavam.`
        : 'Outra pessoa está editando este conteúdo.';
    }
    case 'QUESTION_REFERENCED': {
      const count = readErrorDetail<string[]>(
        err,
        'referencingExamIds',
      )?.length;
      const who =
        count === undefined
          ? 'Algum simulado aponta'
          : `${count} ${count === 1 ? 'simulado aponta' : 'simulados apontam'}`;
      return `Não é possível apagar: ${who} para esta questão. Tire-a desses simulados antes.`;
    }
    default:
      return undefined;
  }
}

/**
 * A questão travada não derruba o lote no back: ela volta como uma linha
 * `{ questionId, error: 'CONTENT_EDIT_LOCKED' }`, e as outras seguem. Quem
 * chama trata o lote como sucesso, então essa linha passaria calada. Aqui ela
 * vira o mesmo 409 de uma rota comum, com a mesma tradução, e as linhas que
 * deram certo voltam para quem precisa delas.
 */
export function throwIfEditLocked<T extends { questionId: string }>(
  results: (T | EditLockedQuestionResult)[],
): T[] {
  const lockedIds = results.flatMap((r) =>
    'error' in r ? [r.questionId] : [],
  );
  if (lockedIds.length > 0) throw new EditLockedResultsError(lockedIds);
  return results as T[];
}

class EditLockedResultsError extends Error {
  readonly response: {
    status: number;
    data: { error: string; message: string; details: { contentIds: string[] } };
  };

  constructor(contentIds: string[]) {
    const data = {
      error: 'CONTENT_EDIT_LOCKED',
      message: '',
      details: { contentIds },
    };
    const message = translateContentLinkError({ response: { data } })!;
    super(message);
    // `message` também no corpo: a rede global (`request-feedback.ts`) lê só
    // ele, e sem isto o toast genérico apareceria antes do traduzido.
    this.response = { status: 409, data: { ...data, message } };
  }
}

/** O `code` do erro de domínio — o back o manda em `error` no corpo. */
export function readErrorCode(err: unknown): string | undefined {
  const body = readErrorBody(err);
  return typeof body?.error === 'string' ? body.error : undefined;
}

export function readErrorDetail<T>(err: unknown, key: string): T | undefined {
  const details = readErrorBody(err)?.details;
  if (details && typeof details === 'object' && key in details) {
    return (details as Record<string, T>)[key];
  }
  return undefined;
}

function readErrorBody(
  err: unknown,
): { error?: unknown; details?: unknown } | undefined {
  if (err && typeof err === 'object') {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data && typeof data === 'object') {
      return data as { error?: unknown; details?: unknown };
    }
  }
  return undefined;
}

/**
 * Aviso de que a ação deu certo, e de que não deu.
 *
 * Existem como duas funções, e não como `toast` importado em cada componente,
 * por dois motivos: a conversão de erro de API em texto legível
 * (`getApiErrorMessage`) fica num lugar só em vez de repetida em cada
 * `onError`; e trocar a biblioteca de aviso passa a ser um arquivo, não uma
 * busca por `toast(` em toda a tela. `sonner` é a escolha que o monorepo já
 * fez (`aristo-dashboards`), com o `<Toaster>` montado em `app.tsx`.
 *
 * **Quando usar:** ação explícita cujo resultado não é visível na tela. Salvar
 * o gabarito, publicar a prova, excluir uma questão. **Quando não usar:** ação
 * que já se anuncia sozinha — mover de posição pisca no destino, e ação em
 * massa tem o painel de fila. Dois avisos para a mesma ação é o ruído que a
 * gente vem cortando.
 */
export function notifyDone(message: string): void {
  toast.success(message);
}

/**
 * Falha de uma ação, avisada com **as duas metades**: o que a pessoa estava
 * fazendo e o que o servidor respondeu.
 *
 * `action` é a ação em texto de gente ("Falha ao publicar a prova", "Questão 12
 * não foi salva") e aparece **sempre**. Antes ela era só um `fallback`: existindo
 * mensagem da API, a ação era descartada e sobrava "O servidor recusou o
 * pedido" — sem dizer o quê nem onde. Numa prova de 80 questões isso é o mesmo
 * que não avisar, e foi o relato de quem opera.
 *
 * Se a falha veio de uma requisição, a rede global
 * (`shared/request-feedback.ts`) já pôs na tela a mensagem crua da API — o `id`
 * derivado do erro faz este aviso **substituir aquele** em vez de empilhar um
 * segundo. Serve também para falha que não é requisição (validação local,
 * clipboard bloqueado), e aí é o único aviso.
 *
 * O `console.error` aqui cobre esse segundo caso: o que não passou por
 * `useMutation` não passou pela rede global, e sem esta linha não deixaria
 * rastro nenhum no devtools.
 */
export function notifyFailed(err: unknown, action: string): void {
  console.error(`[content] ${action}`, err);
  // `getApiErrorMessage(err, '')` vazio = não havia mensagem da API; aí a ação
  // é o aviso inteiro, em vez de virar "Ação: " com o resto em branco.
  const detail = getApiErrorMessage(err, '');
  toast.error(detail ? `${action}: ${detail}` : action, {
    id: errorToastId(err),
  });
}

/**
 * Falha de requisição classificada pelo que o admin pode fazer a respeito.
 * `transient` = o payload está certo, o momento não estava (tentar de novo é a
 * ação); `permanent` = repetir dá o mesmo erro, a ação é corrigir.
 */
export interface RequestFailure {
  kind: 'transient' | 'permanent';
  status?: number;
  message: string;
}

/**
 * 408/425/429 são "tente de novo" por definição; 5xx é o servidor, não o
 * payload. Todo outro 4xx é permanente: 422 de corpo inválido, 413 de payload
 * grande, 415 de formato — reenviar os mesmos bytes dá o mesmo veredito.
 *
 * Hoje isto decide só uma coisa: se o card da pendência oferece o botão de
 * reenviar. Ninguém reenvia sozinho (ver `useRequestQueue`) — por isso o
 * `Retry-After` do servidor deixou de ser lido: não há espera automática para
 * ele temporizar.
 */
const TRANSIENT_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

export function classifyRequestError(
  err: unknown,
  fallback: string,
): RequestFailure {
  const response = (err as { response?: { status?: number } } | null)?.response;
  const status =
    typeof response?.status === 'number' ? response.status : undefined;

  // Sem resposta: rede caiu, CORS, request abortada, servidor fora do ar. Não
  // chegou a falar com o backend, então o payload não é o suspeito — e a
  // mensagem do axios ("Network Error") não diz nada ao admin.
  if (status === undefined) {
    return {
      kind: 'transient',
      message: 'não foi possível falar com o servidor',
    };
  }

  return {
    kind: TRANSIENT_STATUSES.has(status) ? 'transient' : 'permanent',
    status,
    message: getApiErrorMessage(err, fallback),
  };
}

// --- Rótulos PT-BR ---------------------------------------------------------

export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  QUESTION: 'Questão',
  EXAM: 'Prova',
  VIDEO: 'Vídeo',
  FLASHCARD: 'Flashcard',
  OFFICIAL_COMMENT: 'Comentário oficial',
};

export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicado',
  ARCHIVED: 'Arquivado',
};

export const EXAM_KIND_LABELS: Record<ExamKind, string> = {
  REAL_EXAM: 'Prova real',
  MOCK_EXAM: 'Template de simulado',
};

/**
 * O rótulo cita a sigla que o time de conteúdo usa ("R1", "R+") porque é assim
 * que a prova chega no material de origem — o nome por extenso sozinho obrigava
 * a traduzir de cabeça na hora de importar.
 */
export const EXAM_TYPE_LABELS: Record<ExamType, string> = {
  DIRECT_ACCESS: 'Acesso direto (R1)',
  PREREQUISITE: 'Pré-requisito (R+)',
  REVALIDA: 'Revalida',
};

export const CONTENT_RELATION_LABELS: Record<ContentRelation, string> = {
  CONTAINS: 'Contém',
  EXPLAINS: 'Explica',
  REFERENCES: 'Aponta para',
};

/**
 * Tipo de um conteúdo como a tela o nomeia: o simulado é `EXAM` no back, mas
 * para o admin é "Simulado" em todo lugar que diria "Prova" — é isso que
 * diferencia os dois, em vez de um campo afirmando real × simulado.
 */
export function contentTypeLabel(
  type: string,
  examKind: ExamKind | null = null,
): string {
  if (type === 'EXAM' && examKind === 'MOCK_EXAM') return 'Simulado';
  return CONTENT_TYPE_LABELS[type as ContentType] ?? type;
}

/**
 * Nome de um conteúdo onde ele aparece como texto — a ficha, a listagem. É o
 * **único** lugar que conhece este formato:
 *
 * - prova com rótulo: `Prova UNICAMP-SP 2026 – Caderno 02`, ou `Simulado …`
 *   quando `examKind = MOCK_EXAM` (o rótulo vem pronto do back);
 * - questão dentro de uma prova: `Questão 1 - <id completo>` — o id inteiro
 *   para dar para copiar e buscar;
 * - questão fora: `Questão 4b9e3b41` — o id curto na ficha, o completo na
 *   listagem (`idForm`);
 * - o resto, e a prova sem rótulo: `<Tipo> <id completo>`.
 *
 * `position = 0` é posição válida: o teste é `!= null`, nunca falsy.
 */
export function contentDisplayName({
  type,
  id,
  label = null,
  examKind = null,
  position = null,
  idForm = 'short',
}: {
  type: string;
  id: string;
  label?: string | null;
  examKind?: ExamKind | null;
  position?: number | null;
  idForm?: 'short' | 'full';
}): string {
  const typeLabel = contentTypeLabel(type, examKind);
  if (type === 'EXAM' && label) {
    return `${typeLabel} ${label}`;
  }
  if (type === 'QUESTION') {
    if (position != null) return `${typeLabel} ${position} - ${id}`;
    return `${typeLabel} ${idForm === 'short' ? id.slice(0, 8) : id}`;
  }
  return `${typeLabel} ${id}`;
}

/**
 * Título de uma relação **visto do lado de quem abre a ficha**. Saída e
 * chegada usavam o mesmo rótulo, e a questão dizia "Contém <prova>" no bloco
 * de chegada — lido de trás para a frente.
 */
export function relationHeading(
  relation: ContentRelation,
  side: 'source' | 'target',
  itemType: string,
): string {
  if (side === 'target') {
    return {
      CONTAINS: 'Faz parte de',
      REFERENCES: 'Usada nos simulados',
      EXPLAINS: 'Explicada por',
    }[relation];
  }
  return {
    CONTAINS: itemType === 'EXAM' ? 'Questões' : 'Contém',
    REFERENCES: 'Questões do simulado',
    EXPLAINS: 'Explica',
  }[relation];
}

const STATUS_VARIANT: Record<ContentStatus, BadgeVariant> = {
  DRAFT: 'default',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
};

export function ContentStatusBadge({ status }: { status: ContentStatus }) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'default'}>
      {CONTENT_STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

export function ContentTypeBadge({ type }: { type: ContentType }) {
  return <Badge variant="info">{CONTENT_TYPE_LABELS[type] ?? type}</Badge>;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/**
 * A incidência geral, que já chega em 0–100 (não multiplica por 100). `null` é
 * "sem cálculo" e vira `—`; `0` é número calculado e sai `0,00%`.
 */
export function formatPercentage(value: number | null): string {
  if (value === null) return '—';
  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

// --- Blocos de layout reutilizados ----------------------------------------

export function Mono({ children }: { children: React.ReactNode }) {
  return <span className="break-all font-mono text-xs">{children}</span>;
}

export function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
        {label}
      </p>
      <div className="text-(--color-text-primary) text-sm">
        {value === null || value === undefined || value === '' ? (
          <span className="text-(--color-text-secondary) italic">
            Não informado
          </span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

/**
 * Instituição no formato `(SIGLA-UF) NOME` — o MESMO que a coluna `institution`
 * do CSV aceita, para dar para copiar da tabela e colar na planilha.
 *
 * O nome inteiro estoura a largura de uma coluna, então o corte é por CSS
 * (`truncate`) e não na string: o texto que vai para a área de transferência
 * continua completo. O `title` cobre quem só quer ler.
 */
export function InstitutionCell({
  institutionId,
  byId,
  className = 'max-w-[20rem]',
}: {
  institutionId: string | null;
  byId: ReadonlyMap<string, Institution> | undefined;
  className?: string;
}) {
  const label = formatInstitutionLabel(institutionId, byId);
  const [, initials, name] = label.match(/^(\([^)]+\))\s*(.*)$/) ?? [];
  return (
    <span
      title={label}
      className={`block truncate whitespace-nowrap ${className}`}
    >
      <span className="font-medium">{initials ?? label}</span>
      {name && <span className="text-(--color-text-secondary)"> {name}</span>}
    </span>
  );
}

/**
 * O título de um campo de form do conteúdo (Enunciado, Marcações,
 * Alternativas, Assunto): cor primária, `text-lg` e respiro acima e abaixo,
 * para o form se ler pelas partes. É padding, e não margin: o `space-y` do
 * pai sobrescreve o `margin-top`. Abaixo do título da seção (`SectionCard`
 * com `emphasis`, `text-xl`).
 */
export const FIELD_TITLE_CLASS =
  'text-(--color-text-primary) block pt-3 pb-1 text-lg font-semibold';

export function SectionCard({
  title,
  action,
  emphasis = false,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  /**
   * Título em `text-xl`, um passo acima dos títulos dos campos do form
   * (`FIELD_TITLE_CLASS`, `text-lg`). Só a ficha da questão usa: o default não
   * muda as outras telas do content.
   */
  emphasis?: boolean;
  children: React.ReactNode;
}) {
  return (
    <SurfaceCard className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h2
          className={`text-(--color-text-primary) font-semibold ${
            emphasis ? 'text-xl' : 'text-sm'
          }`}
        >
          {title}
        </h2>
        {action}
      </div>
      {children}
    </SurfaceCard>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <SurfaceCard padding="lg" className="text-center">
      <p className="text-(--color-text-secondary) text-sm">{message}</p>
    </SurfaceCard>
  );
}
